/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ActionButton, DefaultButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITheme } from '@fluentui/react/lib/Styling';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { ApprovalPersonaProps } from '../../../models/ApprovalPersonaProps';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { ApproverPeoplePicker } from '../../../views/components';
import { copy, getRecipientTypeLabel, Recipient, RecipientTypeLabel } from '../../../views/Esign/RecipientList';
import { RecipientListComponentProps } from '../../../views/Esign/RecipientList/RecipientList.component';
import { MobileDropdownOption } from '../../components/MobileDropdown/MobileDropdown';
import { MobileDropdownField } from '../../components/MobileDropdownField/MobileDropdownField';
import { MobileTextField, TextFieldType } from '../../components/MobileTextField/MobileTextField';
import { EsignMobileCompleteInOrderToggle } from '../CompleteInOrderToggle/EsignMobileCompleteInOrderToggle';
import { EsignMobileOrderInput } from '../OrderInput/EsignMobileOrderInput';
import { addRecipientsButtonStyles, h3Style, removeButtonStyles, stackStyles } from './RecipientList.styles';

export const RecipientListMobileComponent: React.FC<RecipientListComponentProps> = ({
    recipients,
    availableRoles,
    isAddRecipientDisabled,
    updateRecipient,
    addRecipient,
    removeRecipient,
    completeInOrder,
    toggleCompleteInOrder,
    showNameField,
    recipientTypeLabel,
}: RecipientListComponentProps): JSX.Element => {
    const host = getAppHost();
    const loc = host.getLocale();
    const reorderEcsFlag = host.getEcsConfig().MicrosoftTeamApprovals.EsignMobileReorderEnabled;

    const addClick = (): void => addRecipient();

    return (
        <>
            <EsignMobileCompleteInOrderToggle checked={completeInOrder} onChange={toggleCompleteInOrder} />
            {reorderEcsFlag && completeInOrder && (
                <DefaultButton styles={addRecipientsButtonStyles}>{loc.strings.reorder}</DefaultButton>
            )}
            {recipients.map(
                (recipient: Recipient, index: number): JSX.Element => (
                    <EsignMobileRecipientInput
                        key={index}
                        recipient={recipient}
                        availableRoles={availableRoles}
                        updateRecipient={updateRecipient}
                        removeRecipient={removeRecipient}
                        completeInOrder={completeInOrder}
                        showNameField={showNameField}
                        recipientTypeLabel={recipientTypeLabel}
                    />
                )
            )}
            <DefaultButton styles={addRecipientsButtonStyles} disabled={isAddRecipientDisabled} onClick={addClick}>
                {loc.strings.add_another}
            </DefaultButton>
        </>
    );
};

interface EsignMobileRecipientInput {
    recipient: Recipient;
    availableRoles: string[];
    updateRecipient: (recipient: Recipient, newRecipient: Recipient) => void;
    removeRecipient: (recipient: Recipient) => void;
    completeInOrder: boolean;
    showNameField: boolean;
    recipientTypeLabel: RecipientTypeLabel;
}

const EsignMobileRecipientInput: React.FC<EsignMobileRecipientInput> = ({
    recipient,
    availableRoles,
    updateRecipient,
    removeRecipient,
    completeInOrder,
    showNameField,
    recipientTypeLabel,
}: EsignMobileRecipientInput): JSX.Element => {
    const host = getAppHost();
    const loc = host.getLocale();

    const theme: ITheme = React.useContext(CustomizerContext).customizations.settings.theme;

    const roleOptions = (availableRoles || []).map(
        (option: string, index: number): MobileDropdownOption => ({
            key: index,
            title: option,
            selected: recipient.role === index,
        })
    );

    const onSelectRecipientType = (option: MobileDropdownOption): void => {
        if (option.key !== recipient.role)
            updateRecipient(recipient, {
                ...copy(recipient),
                role: option.key as number,
            });
    };

    const onNameChange = (errorMessage: string, newValue: string): void => {
        updateRecipient(recipient, {
            ...copy(recipient),
            name: newValue,
        });
    };

    const onApproversSelected = (approvers: ApprovalPersonaProps[]): void => {
        updateRecipient(recipient, {
            ...copy(recipient),
            persona: approvers && approvers.length > 0 ? approvers[0] : null,
            name:
                approvers[0]?.email !== approvers[0]?.text
                    ? approvers[0].text
                    : recipient.name || (!showNameField && approvers[0]?.email),
        });
    };

    const recipientTitle = <h3 style={h3Style(theme)}>{loc.strings.recipient_label}</h3>;

    const onRemoveClick = (): void => removeRecipient(recipient);

    const removeRecipientButton = (
        <ActionButton styles={removeButtonStyles} onClick={onRemoveClick}>
            {loc.strings.remove_button_aria_label}
        </ActionButton>
    );

    return (
        <>
            <Stack horizontal horizontalAlign='space-between' styles={stackStyles}>
                <Stack.Item align='center'>{recipientTitle}</Stack.Item>
                <Stack.Item align='center'>{removeRecipientButton}</Stack.Item>
            </Stack>
            {completeInOrder && <EsignMobileOrderInput order={recipient.order} />}
            {showNameField && (
                <MobileTextField
                    title={loc.strings.recipient_list_name_aria_label}
                    placeholder={loc.strings.people_picker_placeholder_text_reassign}
                    value={recipient.name}
                    defaultValue={recipient.name}
                    maxLength={200}
                    updateListener={onNameChange}
                    textFieldType={TextFieldType.Edit}
                />
            )}
            <ApproverPeoplePicker
                placeholderTitle={loc.strings.recipient_list_column_name_email}
                onApproversSelected={onApproversSelected}
                error={null}
                currentSelectedItems={recipient.persona ? [recipient.persona] : []}
                hideLabel={true}
                enableEmail={true}
                itemLimit={1}
                setInitialFocus={recipient.userAdded}
                innerRef={recipient.emailRef}
                required
            />
            <MobileDropdownField
                label={getRecipientTypeLabel(recipientTypeLabel)}
                options={roleOptions}
                onChange={onSelectRecipientType}
            />
        </>
    );
};
